import { useQuery } from "react-query";
import { z } from "zod";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { PuckPage } from "./useCreatePuckPage";
import { useIsCmsEnabled } from "./useIsCmsEnabled";
import i18next from "i18next";

export const getAllPuckPagesQueryKey = "getAllPuckPages";

type GetAllPuckPagesOptions = {
    page?: number;
    itemsPerPage?: number;
    enabled?: boolean;
    search?: string;
    pageType?: "CMS" | "PDF";
    orderBy?: {
        createdAt?: "asc" | "desc";
        updatedAt?: "asc" | "desc";
        title?: "asc" | "desc";
    };
    url?: string;
    status?: "active" | "draft";
    sectorId?: number;
};

export const useGetAllPuckPages = (options: GetAllPuckPagesOptions = {}) => {
    const {
        page = 1,
        itemsPerPage = 20,
        enabled = true,
        search,
        pageType,
        orderBy = { createdAt: "desc" },
        url: filterUrl,
        status,
        sectorId,
    } = options;

    const url = `/api/cms/puck/page`;
    const lang = i18next.language;
    const isCmsEnabled = useIsCmsEnabled();

    const query = useQuery({
        queryKey: [
            getAllPuckPagesQueryKey,
            page,
            itemsPerPage,
            search,
            pageType,
            orderBy,
            filterUrl,
            status,
            sectorId,
            lang,
        ],
        enabled: enabled && isCmsEnabled,
        queryFn: async () => {
            const params: Record<string, any> = {
                page,
                itemsPerPage,
            };

            if (search) {
                params.title = search;
            }

            if (pageType) {
                params.pageType = pageType;
            }

            if (filterUrl) {
                params.url = filterUrl; // exact match on backend
            }

            if (status) {
                params.status = status;
            }

            if (typeof sectorId === "number") {
                params["sectors.id"] = sectorId;
            }

            Object.entries(orderBy).forEach(([key, value]) => {
                if (value) {
                    params[`order[${key}]`] = value;
                }
            });

            const response = await loggedInApiCall.get(url, { params });
            const pagesData = response.data["hydra:member"] || response.data;
            const totalItems = response.data["hydra:totalItems"] || pagesData.length;

            return {
                pages: z.array(PuckPageSchema).parse(pagesData),
                totalItems,
            };
        },
    });

    return {
        pages: query.data?.pages || [],
        totalItems: query.data?.totalItems || 0,
        ...query,
    };
};

export const PuckPageSchema: z.ZodSchema<PuckPage> = z.object({
    slug: z.string(),
    title: z.string(),
    content: z.string(),
    locale: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    url: z.string().optional(),
    pageType: z.enum(["CMS", "PDF"]).optional(),
    status: z.enum(["active", "draft"]).optional(),
    allowedRoles: z.array(z.string()).nullish(),
    sectors: z.array(z.string()).nullish(),
});
